import {use} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useEnvironment} from './use-environment'
import {SessionContext} from '../contexts/SessionContext'
import type {Environment} from '../types/environment'
import type {Session} from '../types/session'

export interface AhpSessionEligibility {
  /**
   * True when the active session is bound to an MC environment. This is an
   * **identity** signal derived synchronously from the session's
   * `environment_id` — it does not wait for the environments query and does not
   * depend on whether the host is currently reachable — so an AHP session is
   * recognised as AHP on first render (correct log renderer, single-session
   * chrome, steering routing, repo-permission bypass) with no flash of the
   * non-AHP UI. Use this for anything that describes what the session *is*.
   */
  isAhpSession: boolean
  /**
   * True when the session is AHP **and** its environment is resolved, speaks AHP,
   * and is currently online, so the live AHP relay can be used. Always implies
   * {@link isAhpSession}. Depends on the environments query, so it may flip from
   * `false` to `true` once that resolves. Use this for anything that needs to
   * reach the host right now: the live relay event source, the native
   * (relay-backed) timeline, and steering dispatch.
   */
  reachable: boolean
  /** The environment id the session is bound to, when one is known. */
  environmentId: string | undefined
  /** The resolved environment record, when the environments query has populated. */
  environment: Environment | undefined
  /** True while the environments list is still loading; `reachable` may flip when it resolves. */
  loading: boolean
}

/**
 * Pure decision for {@link useIsAhpSession}, split out so it can be unit-tested
 * without wiring the environments query, feature flags, and session context.
 *
 * `isAhpSession` (identity) depends only on the session being bound to an
 * environment (`environmentId`), so it resolves synchronously without the
 * environments query. `reachable` (connectivity) additionally requires the
 * resolved environment to speak AHP and be online.
 */
export function resolveAhpEligibility(
  flagOn: boolean,
  environmentId: string | undefined,
  environment: Environment | undefined,
): {isAhpSession: boolean; reachable: boolean} {
  const isAhpSession = flagOn && !!environmentId
  const reachable = isAhpSession && !!environment?.capabilities?.ahp_version && environment.status === 'online'
  return {isAhpSession, reachable}
}

/**
 * Decides whether the active session is AHP-backed and whether its host is
 * currently reachable.
 *
 * Consumers must branch on the right signal: {@link AhpSessionEligibility.isAhpSession}
 * for session identity (log renderer, chrome, routing) and
 * {@link AhpSessionEligibility.reachable} for anything requiring a live relay
 * connection (event streaming, native timeline, steering). Bundling both into
 * one flag would make an offline AHP session silently render as a legacy session.
 *
 * The bound environment is resolved by id via {@link useEnvironment}, not by
 * scanning the list, which omits per-session `managed-sandbox` allocations. Only
 * `reachable` depends on that lookup; identity resolves synchronously from
 * `environment_id`.
 *
 * Gated by the environments feature flag (the same flag that gates the
 * environments query itself).
 */
export function useIsAhpSession(session: Session | null | undefined): AhpSessionEligibility {
  const environmentId = session?.environment_id
  const environmentsFlagOn = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const {data: environment, isLoading} = useEnvironment(environmentId, {
    enabled: environmentsFlagOn && !!environmentId,
  })
  const {isAhpSession, reachable} = resolveAhpEligibility(environmentsFlagOn, environmentId, environment ?? undefined)
  return {
    isAhpSession,
    reachable,
    environmentId,
    environment: environment ?? undefined,
    loading: environmentsFlagOn && !!environmentId && isLoading,
  }
}

/**
 * Convenience hook for callers that already use {@link SessionContext}.
 * Equivalent to `useIsAhpSession(use(SessionContext)?.session ?? null)`.
 */
export function useIsAhpSessionFromContext(): AhpSessionEligibility {
  const ctx = use(SessionContext)
  return useIsAhpSession(ctx?.session ?? null)
}
